import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useCloudStorage } from '../hooks/useCloudStorage'
import { useLocalMode } from './LocalModeContext'
import syncService from '../services/syncService'
import frontendSync from '../services/frontendSync'
import { SYNC_CONFIG } from '../services/syncConfig'
import toast from 'react-hot-toast'

/**
 * Sync Context for managing synchronization between local and cloud storage
 */

const SyncContext = createContext({})

export const useSync = () => {
  const context = useContext(SyncContext)
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider')
  }
  return context
}

export const SyncProvider = ({ children }) => {
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState(null)
  const [conflicts, setConflicts] = useState([])
  const [lastSyncTime, setLastSyncTime] = useState(null)
  const [autoSync, setAutoSync] = useState(true)
  const [syncInterval, setSyncInterval] = useState(5 * 60 * 1000) // 5 minutes

  const cloudStorage = useCloudStorage()
  const { isLocalMode } = useLocalMode()

  // Initialize sync service
  useEffect(() => {
    if (cloudStorage) {
      if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        console.log('🚀 SyncContext: Using frontend-only sync service')
        // Frontend sync doesn't need explicit initialization with cloudStorage
        // It uses googleDriveAPI directly
      } else {
        console.log('🚀 SyncContext: Using legacy backend sync service')
        syncService.initialize(cloudStorage)
      }
    }
  }, [cloudStorage])

  // Auto-sync interval - ENABLED: Frontend handles sync automatically
  useEffect(() => {
    if (!autoSync || !cloudStorage.isConnected || isLocalMode) {
      console.log('🔍 Sync: Auto-sync conditions not met', { autoSync, connected: cloudStorage.isConnected, isLocalMode })
      return
    }
    
    console.log('🚀 Sync: Frontend auto-sync enabled - performing background sync every', syncInterval / 60000, 'minutes')
    
    const interval = setInterval(() => {
      if (!isSyncing) {
        performSync()
      }
    }, syncInterval)
    
    return () => clearInterval(interval)
  }, [autoSync, cloudStorage.isConnected, isLocalMode, syncInterval, isSyncing])

  // Update sync status
  useEffect(() => {
    const updateStatus = () => {
      if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        // For frontend sync, status is managed by individual contexts
        setSyncStatus({
          isActive: false,
          currentOperation: null,
          progress: 100,
          message: 'Frontend sync is automatic'
        })
        setConflicts([]) // Frontend sync handles conflicts differently
      } else {
        const status = syncService.getSyncStatus()
        setSyncStatus(status)
        setIsSyncing(status.isSyncing)
        setLastSyncTime(status.lastSyncTime)
        
        const currentConflicts = syncService.getConflicts()
        setConflicts(currentConflicts)
      }
    }

    // Update status immediately
    updateStatus()

    // Set up interval to check status
    const statusInterval = setInterval(updateStatus, 1000)
    
    return () => clearInterval(statusInterval)
  }, [])

  /**
   * Perform full synchronization - ENABLED: Frontend handles sync automatically
   */
  const performSync = useCallback(async () => {
    try {
      if (!cloudStorage.isConnected) {
        toast.error('Not connected to cloud storage')
        return { success: false, error: 'Not connected' }
      }
      if (isLocalMode) {
        toast.error('Sync not available in local mode')
        return { success: false, error: 'Local mode active' }
      }
      
      console.log('🔄 Sync: Starting synchronization')
      setIsSyncing(true)
      
      let result
      if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        console.log('🚀 Sync: Using frontend-only sync')
        // Frontend sync is handled by NotesContext and FoldersContext directly
        // No need for explicit sync calls - they handle their own sync
        result = { success: true, message: 'Frontend sync is automatic' }
      } else {
        console.log('🚀 Sync: Using legacy backend sync')
        result = await syncService.fullSync()
      }
      
      setLastSyncTime(new Date())
      
      if (result.success) {
        toast.success('Sync completed successfully')
      }
      
      return result

    } catch (error) {
      console.error('❌ Sync: Synchronization failed:', error)
      toast.error('Sync failed: ' + error.message)
      return { success: false, error: error.message }
    } finally {
      setIsSyncing(false)
    }
  }, [cloudStorage.isConnected, isLocalMode])

  /**
   * Sync single note to cloud
   */
  const syncNoteToCloud = useCallback(async (note) => {
    try {
      if (!cloudStorage.isConnected || isLocalMode) {
        return { success: false, error: 'Cloud not available' }
      }

      console.log('📤 Sync: Syncing note to cloud:', note.noteId)
      
      let result
      if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        console.log('🚀 Sync: Frontend sync handles note sync automatically')
        // Frontend sync is handled automatically by NotesContext
        result = { success: true, message: 'Frontend sync is automatic' }
      } else {
        result = await syncService.syncNoteToCloud(note)
      }
      
      if (result.success) {
        toast.success('Note synced to cloud')
      }
      
      return result

    } catch (error) {
      console.error('❌ Sync: Failed to sync note to cloud:', error)
      toast.error('Failed to sync note to cloud')
      return { success: false, error: error.message }
    }
  }, [cloudStorage.isConnected, isLocalMode])

  /**
   * Queue note for sync
   */
  const queueNoteSync = useCallback((note, action = 'upload') => {
    if (!cloudStorage.isConnected || isLocalMode) {
      return
    }

    syncService.addToSyncQueue({
      type: 'note',
      action,
      data: note
    })
  }, [cloudStorage.isConnected, isLocalMode])

  /**
   * Resolve sync conflict
   */
  const resolveConflict = useCallback(async (conflict, resolution) => {
    try {
      console.log('🔧 Sync: Resolving conflict:', conflict.noteId, resolution)
      
      const result = await syncService.resolveConflict(conflict, resolution)
      
      if (result.success) {
        // Update conflicts list
        setConflicts(current => current.filter(c => c.noteId !== conflict.noteId))
        toast.success('Conflict resolved successfully')
      }
      
      return result

    } catch (error) {
      console.error('❌ Sync: Failed to resolve conflict:', error)
      toast.error('Failed to resolve conflict')
      return { success: false, error: error.message }
    }
  }, [])

  /**
   * Set conflict resolution strategy
   */
  const setConflictResolutionStrategy = useCallback((strategy) => {
    syncService.setConflictResolutionStrategy(strategy)
    toast.success(`Conflict resolution set to: ${strategy}`)
  }, [])

  /**
   * Enable/disable auto-sync
   */
  const toggleAutoSync = useCallback((enabled) => {
    setAutoSync(enabled)
    localStorage.setItem('autoSync', enabled.toString())
    
    if (enabled) {
      toast.success('Auto-sync enabled')
    } else {
      toast.success('Auto-sync disabled')
    }
  }, [])

  /**
   * Set sync interval
   */
  const setSyncIntervalMinutes = useCallback((minutes) => {
    const intervalMs = minutes * 60 * 1000
    setSyncInterval(intervalMs)
    localStorage.setItem('syncInterval', intervalMs.toString())
    toast.success(`Sync interval set to ${minutes} minutes`)
  }, [])

  /**
   * Get sync statistics
   */
  const getSyncStats = useCallback(() => {
    return {
      isConnected: cloudStorage.isConnected,
      provider: cloudStorage.connectedProvider,
      isSyncing,
      lastSyncTime,
      conflictsCount: conflicts.length,
      autoSyncEnabled: autoSync,
      syncIntervalMinutes: syncInterval / (60 * 1000),
      queueLength: syncStatus?.queueLength || 0
    }
  }, [cloudStorage, isSyncing, lastSyncTime, conflicts, autoSync, syncInterval, syncStatus])

  // Load settings from localStorage
  useEffect(() => {
    const savedAutoSync = localStorage.getItem('autoSync')
    if (savedAutoSync !== null) {
      setAutoSync(savedAutoSync === 'true')
    }

    const savedInterval = localStorage.getItem('syncInterval')
    if (savedInterval) {
      setSyncInterval(parseInt(savedInterval, 10))
    }
  }, [])

  const value = {
    // State
    isSyncing,
    syncStatus,
    conflicts,
    lastSyncTime,
    autoSync,
    syncInterval: syncInterval / (60 * 1000), // Return in minutes

    // Actions
    performSync,
    syncNoteToCloud,
    queueNoteSync,
    resolveConflict,
    setConflictResolutionStrategy,
    toggleAutoSync,
    setSyncIntervalMinutes,
    getSyncStats,
  }

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  )
} 